import React, { useState, useEffect, useCallback } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { cantieri } from '../../utils/api';
import { formatDate, formatDateShort } from '../../utils/date';
import { GripVertical } from 'lucide-react';
import PianoLavoriPDF, { slugify } from './PianoLavoriPDF';

const STATUS_COLORS = {
  planned: '#F0AD4E', in_progress: '#4A7CFF', at_risk: '#F5A623',
  done: '#2EA084', blocked: '#E85D5D',
};
const STATUS_LABEL = {
  planned: 'Pianificata', in_progress: 'In corso', at_risk: 'A rischio',
  done: 'Completata', blocked: 'Bloccata',
};
const TASK_STATUS_LABEL = {
  todo: 'Da fare', in_progress: 'In corso', done: 'Completata', skipped: 'Saltata',
};

const today = () => new Date().toISOString().slice(0, 10);
const in30 = () => {
  const d = new Date(); d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

export default function PianoLavoriTab({ cantiereId, dashData, onRefresh, onSilentRefresh }) {
  const [piano, setPiano] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFase, setExpandedFase] = useState(null);
  const [showCreaPiano, setShowCreaPiano] = useState(false);
  const [showAddFase, setShowAddFase] = useState(false);
  const [addTaskFaseId, setAddTaskFaseId] = useState(null);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [editingFaseId, setEditingFaseId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // Riordino fasi via drag&drop: indice trascinato + indice sotto il cursore
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const fetchPiano = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cantieri.getPiano(cantiereId);
      setPiano(res.data);
    } catch {
      setError('Errore nel caricamento del piano lavori.');
    } finally {
      setLoading(false);
    }
  }, [cantiereId]);

  useEffect(() => { fetchPiano(); }, [fetchPiano]);

  // Dopo ogni mutazione del piano: ricarica la tab E il dashboard del modale
  // (onRefresh) → Panoramica/Spese restano allineate in tempo reale.
  const refreshAll = useCallback(async () => {
    await fetchPiano();
    if (onRefresh) onRefresh();
  }, [fetchPiano, onRefresh]);

  const handleCreaPiano = async (data) => {
    try {
      await cantieri.createPiano(cantiereId, data);
      setShowCreaPiano(false);
      refreshAll();
    } catch { /* global handler */ }
  };

  const handleAddFase = async (data) => {
    try {
      await cantieri.createFase(cantiereId, data);
      setShowAddFase(false);
      refreshAll();
    } catch { /* global handler */ }
  };

  const handleDeleteFase = async (faseId) => {
    if (!window.confirm('Eliminare questa fase e tutti i suoi task?')) return;
    try {
      await cantieri.deleteFase(cantiereId, faseId);
      refreshAll();
    } catch { /* global handler */ }
  };

  const handleUpdateFase = async (faseId, data) => {
    try {
      await cantieri.updateFase(cantiereId, faseId, data);
      setEditingFaseId(null);
      refreshAll();
    } catch { /* global handler */ }
  };

  // Budget costi lavoro (PianoLavori.budget_totale) — inline edit sul KPI.
  const handleUpdateBudget = async (value) => {
    try {
      await cantieri.updatePiano(cantiereId, { budget_totale: value === '' || value == null ? null : value });
      refreshAll();
    } catch { /* global handler */ }
  };

  // Riordino fasi (frecce o drag&drop) — OTTIMISTICO: aggiorna SUBITO lo stato
  // locale (niente re-fetch con spinner → niente sfarfallio) e persiste il nuovo
  // `ordine` in background. PATCH solo le fasi il cui ordine cambia. Se la
  // persistenza fallisce, si risincronizza dal server.
  const persistReorder = (reordered) => {
    const updates = reordered
      .map((f, i) => (f.ordine !== i ? cantieri.updateFase(cantiereId, f.id, { ordine: i }) : null))
      .filter(Boolean);
    if (updates.length === 0) return;
    setPiano(prev => (prev ? { ...prev, fasi: reordered.map((f, i) => ({ ...f, ordine: i })) } : prev));
    Promise.all(updates)
      .then(() => { if (onSilentRefresh) onSilentRefresh(); })  // riallinea Panoramica/gantt senza spinner
      .catch(() => fetchPiano());
  };

  // Riordino via frecce ↑/↓ — scambio adiacente.
  const handleMoveFase = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= fasi.length) return;
    const reordered = [...fasi];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    persistReorder(reordered);
  };

  // Riordino via drag&drop — sposta from→to.
  const handleReorderFase = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= fasi.length || to >= fasi.length) return;
    const reordered = [...fasi];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    persistReorder(reordered);
  };

  const handleAddTask = async (faseId, data) => {
    try {
      await cantieri.createTask(cantiereId, faseId, data);
      setAddTaskFaseId(null);
      refreshAll();
    } catch { /* global handler */ }
  };

  const handleCompletaTask = async (taskId) => {
    try {
      await cantieri.completaTask(cantiereId, taskId);
      refreshAll();
    } catch { /* global handler */ }
  };

  const handleAddMilestone = async (data) => {
    try {
      await cantieri.createMilestone(cantiereId, data);
      setShowAddMilestone(false);
      refreshAll();
    } catch { /* global handler */ }
  };

  const handleUpdateMilestone = async (milestoneId, data) => {
    try {
      await cantieri.updateMilestone(cantiereId, milestoneId, data);
      refreshAll();
    } catch { /* global handler */ }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if (!window.confirm('Eliminare questa milestone?')) return;
    try {
      await cantieri.deleteMilestone(cantiereId, milestoneId);
      refreshAll();
    } catch { /* global handler */ }
  };

  const handleImportaContratto = async (file) => {
    setImporting(true);
    try {
      const res = await cantieri.importaContratto(cantiereId, file);
      const d = res.data;
      const msg = (d.fasi_create?.length || 0) + ' fasi e ' + (d.milestone_create?.length || 0) + ' milestone importate dal contratto.';
      alert(msg);
      refreshAll();
    } catch {
      alert('Errore durante l\'importazione. Riprova.');
    } finally { setImporting(false); }
  };

  if (loading) return <div style={{ padding: 20, color: 'var(--ds-muted)' }}>Caricamento piano lavori...</div>;
  if (error) return <div style={{ padding: 20, color: 'var(--ds-danger)' }}>{error}</div>;

  // Piano non esiste — mostra form creazione
  if (!piano || !piano.esiste) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
        <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ds-text)', marginBottom: 8 }}>
          Nessun piano lavori
        </div>
        <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginBottom: 16 }}>
          Crea un piano per organizzare fasi, task e milestone del cantiere.
        </div>
        {!showCreaPiano ? (
          <button onClick={() => setShowCreaPiano(true)} style={btnPrimary}>
            + Crea piano lavori
          </button>
        ) : (
          <CreaPianoForm onSubmit={handleCreaPiano} onCancel={() => setShowCreaPiano(false)} />
        )}
      </div>
    );
  }

  const fasi = piano.fasi || [];
  const milestone = piano.milestone || [];

  const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  // Durata: da date contrattuali o calcolata dalle fasi
  const durataGiorni = (() => {
    if (piano.durata_giorni) return piano.durata_giorni;
    if (fasi.length === 0) return 0;
    const dates = fasi.flatMap(f => [new Date(f.data_inizio_pianificata), new Date(f.data_fine_pianificata)]);
    const min = Math.min(...dates.map(d => d.getTime()));
    const max = Math.max(...dates.map(d => d.getTime()));
    return Math.round((max - min) / 86400000);
  })();

  const giorniPassati = (() => {
    const inizio = piano.data_inizio_contrattuale
      || (fasi.length > 0 ? fasi[0].data_inizio_pianificata : null);
    if (!inizio) return 0;
    const diff = Math.floor((Date.now() - new Date(inizio).getTime()) / 86400000);
    return Math.max(0, Math.min(diff, durataGiorni));
  })();

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>Piano Lavori</div>
          <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>
            {fasi.length} fasi · {piano.percentuale_avanzamento}% completato · {STATUS_LABEL[piano.status_semaforo] || '—'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <PDFDownloadLink
            document={<PianoLavoriPDF piano={piano} cantiere={dashData?.cantiere} azienda={dashData?.azienda} />}
            fileName={'piano-lavori-' + slugify(dashData?.cantiere?.titolo || 'cantiere') + '-' + new Date().toISOString().slice(0, 10) + '.pdf'}
          >
            {({ loading }) => (
              <button style={btnSmall} disabled={loading}>{loading ? 'Preparazione...' : '📄 Scarica PDF'}</button>
            )}
          </PDFDownloadLink>
          <label style={{ ...btnSmall, display: 'flex', alignItems: 'center', gap: 4, cursor: importing ? 'wait' : 'pointer', opacity: importing ? 0.5 : 1 }}>
            📄 {importing ? 'Importando...' : 'Importa da contratto'}
            <input type="file" accept=".pdf" hidden disabled={importing} onChange={e => { if (e.target.files?.[0]) handleImportaContratto(e.target.files[0]); e.target.value = ''; }} />
          </label>
          <button onClick={() => setShowAddFase(true)} style={btnPrimary}>+ Aggiungi fase</button>
        </div>
      </div>

      {/* 4 KPI box */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1,
        background: 'var(--ds-border)', borderRadius: 'var(--radius-base)', overflow: 'hidden', marginBottom: 20,
      }}>
        <div style={{ padding: '14px 16px', background: 'var(--chip)' }}>
          <div style={{ fontSize: 10, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Budget</div>
          <BudgetEditable value={piano.budget_totale} onSave={handleUpdateBudget} />
        </div>
        <div style={{ padding: '14px 16px', background: 'var(--chip)' }}>
          <div style={{ fontSize: 10, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Speso</div>
          <div style={{ fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ds-danger)' }}>{fmtEur(piano.budget_speso)}</div>
        </div>
        <div style={{ padding: '14px 16px', background: 'var(--chip)' }}>
          <div style={{ fontSize: 10, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Giorni</div>
          <div style={{ fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>{giorniPassati}/{durataGiorni}</div>
        </div>
        <div style={{ padding: '14px 16px', background: 'var(--chip)' }}>
          <div style={{ fontSize: 10, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Stato</div>
          <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-display)', color: STATUS_COLORS[piano.status_semaforo] }}>
            {piano.status_semaforo === 'at_risk' ? '⚠️ ' : ''}{STATUS_LABEL[piano.status_semaforo] || '—'}
          </div>
        </div>
      </div>

      {/* Tabella marcia */}
      {fasi.length > 0 && <TabellaMarcia fasi={fasi} durataGiorni={durataGiorni} pianoInizio={piano.data_inizio_contrattuale} />}

      {/* Banner post-import: date stimate da AI — dismissabile */}
      {!bannerDismissed && fasi.some(f => f.source === 'contract' && !f.data_inizio_effettiva) && (
        <div style={{
          padding: '10px 14px', borderRadius: 'var(--radius-base)', marginBottom: 16,
          background: 'rgba(232,145,42,0.12)', border: '1px solid var(--warn)',
          fontSize: 12, color: 'var(--warn)', lineHeight: 1.5,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}>
          <span>⚠️ Le date delle fasi sono state stimate automaticamente dal contratto. Verifica e aggiusta le date prima di iniziare i lavori.</span>
          <button onClick={async () => {
            // PATCH data_inizio_effettiva su tutte le fasi contract senza data effettiva
            const fasiDaConfermare = fasi.filter(f => f.source === 'contract' && !f.data_inizio_effettiva);
            for (const f of fasiDaConfermare) {
              await cantieri.updateFase(cantiereId, f.id, {
                data_inizio_effettiva: f.data_inizio_pianificata,
              }).catch(() => {});
            }
            setBannerDismissed(true);
            refreshAll(); // refresh tab + dashboard (il signal aggiorna anche il calendario)
          }} style={{
            padding: '4px 12px', borderRadius: 4, border: '1px solid var(--warn)',
            background: 'var(--ds-bg)', color: 'var(--warn)', cursor: 'pointer', fontSize: 11,
            whiteSpace: 'nowrap', fontWeight: 500,
          }}>
            ✓ Date verificate
          </button>
        </div>
      )}

      {/* Fasi espandibili */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, marginTop: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>🔨 Dettaglio fasi</div>
      </div>

      {showAddFase && (
        <AddFaseForm onSubmit={handleAddFase} onCancel={() => setShowAddFase(false)} ordine={fasi.length} />
      )}

      {fasi.length === 0 && !showAddFase ? (
        <div style={{ fontSize: 13, color: 'var(--ds-muted)', padding: '12px 0' }}>Nessuna fase definita.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          {fasi.map((fase, idx) => (
            <FaseRow
              key={fase.id}
              fase={fase}
              canMoveUp={idx > 0}
              canMoveDown={idx < fasi.length - 1}
              onMoveUp={() => handleMoveFase(idx, -1)}
              onMoveDown={() => handleMoveFase(idx, 1)}
              index={idx}
              isDragging={dragIdx === idx}
              isDragOver={dragOverIdx === idx && dragIdx !== null && dragIdx !== idx}
              onDragStartFase={() => setDragIdx(idx)}
              onDragOverFase={(e) => { e.preventDefault(); if (dragOverIdx !== idx) setDragOverIdx(idx); }}
              onDropFase={(e) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== idx) handleReorderFase(dragIdx, idx); setDragIdx(null); setDragOverIdx(null); }}
              onDragEndFase={() => { setDragIdx(null); setDragOverIdx(null); }}
              expanded={expandedFase === fase.id}
              onToggle={() => setExpandedFase(expandedFase === fase.id ? null : fase.id)}
              onCompletaTask={handleCompletaTask}
              onDelete={() => handleDeleteFase(fase.id)}
              isEditing={editingFaseId === fase.id}
              onToggleEdit={() => setEditingFaseId(editingFaseId === fase.id ? null : fase.id)}
              onUpdateFase={(data) => handleUpdateFase(fase.id, data)}
              showAddTask={addTaskFaseId === fase.id}
              onToggleAddTask={() => setAddTaskFaseId(addTaskFaseId === fase.id ? null : fase.id)}
              onAddTask={(data) => handleAddTask(fase.id, data)}
            />
          ))}
        </div>
      )}

      {/* Milestone */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>🏁 Milestone</div>
        <button onClick={() => setShowAddMilestone(true)} style={btnSmall}>+ Aggiungi milestone</button>
      </div>

      {showAddMilestone && (
        <AddMilestoneForm onSubmit={handleAddMilestone} onCancel={() => setShowAddMilestone(false)} />
      )}

      {milestone.length === 0 && !showAddMilestone ? (
        <div style={{ fontSize: 13, color: 'var(--ds-muted)', padding: '12px 0' }}>Nessuna milestone definita.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {milestone.map(m => (
            <MilestoneRow
              key={m.id}
              milestone={m}
              onToggle={() => handleUpdateMilestone(m.id, { raggiunta: !m.raggiunta })}
              onUpdate={(data) => handleUpdateMilestone(m.id, data)}
              onDelete={() => handleDeleteMilestone(m.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Budget KPI inline-editable (PianoLavori.budget_totale) ──────────────────
function BudgetEditable({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const start = () => { setVal(value == null ? '' : String(value)); setEditing(true); };
  const commit = () => { setEditing(false); onSave(val.trim()); };
  if (editing) {
    return (
      <input
        type="number" step="0.01" min="0" autoFocus value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === 'Escape') { setEditing(false); }
        }}
        style={{ fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)', width: '100%', border: '1px solid var(--brand)', borderRadius: 6, padding: '2px 6px', outline: 'none', boxSizing: 'border-box' }}
      />
    );
  }
  return (
    <div
      onClick={start} title="Click per modificare il budget"
      style={{ fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      {Number(value || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ds-muted)" strokeWidth="2">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4z" />
      </svg>
    </div>
  );
}

// ── Stili bottoni ──────────────────────────────────────────────────────────

const btnPrimary = {
  padding: '10px 20px', borderRadius: 6, border: 'none', minHeight: 44,
  background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500,
};
const btnSmall = {
  padding: '6px 14px', borderRadius: 6, border: '1px solid var(--ds-border)',
  background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 12,
};
const btnCancel = {
  padding: '8px 16px', borderRadius: 6, border: '1px solid var(--ds-border)',
  background: 'var(--ds-bg)', color: 'var(--ds-muted)', cursor: 'pointer', fontSize: 12,
};
const inputStyle = {
  padding: '8px 12px', borderRadius: 6, border: '1px solid var(--ds-border)',
  fontSize: 13, width: '100%', background: 'var(--ds-bg)', color: 'var(--ds-text)',
};

// ── Form Crea Piano ────────────────────────────────────────────────────────

function CreaPianoForm({ onSubmit, onCancel }) {
  const [form, setForm] = useState({
    data_inizio_contrattuale: today(),
    data_fine_contrattuale: in30(),
    budget_totale: '',
    note: '',
  });

  return (
    <div style={{ textAlign: 'left', maxWidth: 400, margin: '0 auto', padding: 20, border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)' }}>
      <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)', marginBottom: 12 }}>Nuovo piano lavori</div>
      <label style={labelStyle}>Data inizio contrattuale</label>
      <input type="date" value={form.data_inizio_contrattuale} onChange={e => setForm({ ...form, data_inizio_contrattuale: e.target.value })} style={{ ...inputStyle, marginBottom: 10 }} />
      <label style={labelStyle}>Data fine contrattuale</label>
      <input type="date" value={form.data_fine_contrattuale} onChange={e => setForm({ ...form, data_fine_contrattuale: e.target.value })} style={{ ...inputStyle, marginBottom: 10 }} />
      <label style={labelStyle}>Budget totale (EUR)</label>
      <input type="number" step="0.01" placeholder="es. 25000" value={form.budget_totale} onChange={e => setForm({ ...form, budget_totale: e.target.value })} style={{ ...inputStyle, marginBottom: 10 }} />
      <label style={labelStyle}>Note</label>
      <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Note sul piano..." rows={3} style={{ ...inputStyle, marginBottom: 14, resize: 'vertical' }} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={btnCancel}>Annulla</button>
        <button onClick={() => onSubmit({
          ...form,
          budget_totale: form.budget_totale ? parseFloat(form.budget_totale) : null,
        })} style={btnPrimary}>Crea piano</button>
      </div>
    </div>
  );
}

// ── Form Aggiungi Fase ─────────────────────────────────────────────────────

function AddFaseForm({ onSubmit, onCancel, ordine }) {
  const [form, setForm] = useState({
    nome: '', data_inizio_pianificata: today(), data_fine_pianificata: in30(),
    costo_previsto: '', descrizione: '', responsabile_esterno: '',
  });

  return (
    <div style={{ padding: 16, border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', marginBottom: 12, background: 'var(--chip)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)', marginBottom: 10 }}>Nuova fase</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={labelStyle}>Nome fase *</label>
          <input placeholder="es. Demolizioni" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Chi (responsabile)</label>
          <input placeholder="es. Squadra A, Titolare" value={form.responsabile_esterno} onChange={e => setForm({ ...form, responsabile_esterno: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Inizio</label>
          <input type="date" value={form.data_inizio_pianificata} onChange={e => setForm({ ...form, data_inizio_pianificata: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Fine</label>
          <input type="date" value={form.data_fine_pianificata} onChange={e => setForm({ ...form, data_fine_pianificata: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Costo previsto</label>
          <input type="number" step="0.01" placeholder="0.00" value={form.costo_previsto} onChange={e => setForm({ ...form, costo_previsto: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Descrizione</label>
          <input placeholder="Opzionale" value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} style={inputStyle} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button onClick={onCancel} style={btnCancel}>Annulla</button>
        <button disabled={!form.nome} onClick={() => onSubmit({
          ...form, ordine,
          costo_previsto: form.costo_previsto ? parseFloat(form.costo_previsto) : 0,
        })} style={{ ...btnPrimary, opacity: form.nome ? 1 : 0.5 }}>Aggiungi fase</button>
      </div>
    </div>
  );
}

// ── Form Aggiungi Task ─────────────────────────────────────────────────────

function AddTaskInline({ onSubmit, onCancel }) {
  const [nome, setNome] = useState('');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && nome.trim()) { onSubmit({ nome: nome.trim() }); setNome(''); }
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div style={{ display: 'flex', gap: 8, padding: '8px 0' }}>
      <input
        autoFocus placeholder="Nome task..." value={nome}
        onChange={e => setNome(e.target.value)} onKeyDown={handleKeyDown}
        style={{ ...inputStyle, flex: 1 }}
      />
      <button disabled={!nome.trim()} onClick={() => { onSubmit({ nome: nome.trim() }); setNome(''); }}
        style={{ ...btnPrimary, padding: '6px 14px', fontSize: 12 }}>+</button>
      <button onClick={onCancel} style={{ ...btnCancel, padding: '6px 12px' }}>×</button>
    </div>
  );
}

// ── Form Aggiungi Milestone ────────────────────────────────────────────────

function AddMilestoneForm({ onSubmit, onCancel }) {
  const [form, setForm] = useState({ nome: '', data: today(), importo_sal: '' });

  return (
    <div style={{ padding: 16, border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', marginBottom: 12, background: 'var(--chip)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <input placeholder="Nome milestone *" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={inputStyle} />
        <input type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} style={inputStyle} />
        <input type="number" step="0.01" placeholder="Importo SAL" value={form.importo_sal} onChange={e => setForm({ ...form, importo_sal: e.target.value })} style={inputStyle} />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <button onClick={onCancel} style={btnCancel}>Annulla</button>
        <button disabled={!form.nome} onClick={() => onSubmit({
          ...form, importo_sal: form.importo_sal ? parseFloat(form.importo_sal) : null,
        })} style={{ ...btnPrimary, opacity: form.nome ? 1 : 0.5, padding: '8px 16px', fontSize: 12 }}>Aggiungi</button>
      </div>
    </div>
  );
}

// ── Componenti helper ──────────────────────────────────────────────────────

const labelStyle = { fontSize: 11, color: 'var(--ds-muted)', display: 'block', marginBottom: 3, textTransform: 'uppercase' };

// ── Tabella Marcia (Gantt tabellare) ────────────────────────────────────────

function TabellaMarcia({ fasi, durataGiorni, pianoInizio }) {
  const fmtLabel = (ts) => new Date(ts).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  const minDate = pianoInizio ? new Date(pianoInizio).getTime() : Math.min(...fasi.map(f => new Date(f.data_inizio_pianificata).getTime()));
  const maxDate = Math.max(...fasi.map(f => new Date(f.data_fine_pianificata).getTime()));
  const range = maxDate - minDate || 1;
  const todayTs = new Date().setHours(0, 0, 0, 0);
  const todayPct = Math.min(100, Math.max(0, ((todayTs - minDate) / range) * 100));

  // Asse X: 5 etichette equidistanti
  const dateLabels = [];
  for (let i = 0; i < 5; i++) {
    const ts = minDate + (range * i / 4);
    dateLabels.push({ pct: (i / 4) * 100, label: fmtLabel(ts) });
  }

  return (
    <div style={{ border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)', background: 'var(--chip)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--ds-border)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>📋 Tabella marcia</div>
        <div style={{ fontSize: 11, color: 'var(--ds-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ds-danger)', display: 'inline-block' }} />
          oggi · {durataGiorni} giorni totali
        </div>
      </div>

      {/* Colonne header */}
      <div style={{ display: 'grid', gridTemplateColumns: '140px 90px 1fr 100px', padding: '8px 16px', borderBottom: '1px solid var(--ds-border)', fontSize: 10, color: 'var(--ds-muted)', textTransform: 'uppercase' }}>
        <span>Fase</span><span>Stato</span><span>Timeline</span><span style={{ textAlign: 'right' }}>Chi</span>
      </div>

      {/* Asse X date — sotto l'header, sopra le righe */}
      <div style={{ display: 'grid', gridTemplateColumns: '140px 90px 1fr 100px', padding: '0 16px' }}>
        <div /><div />
        <div style={{ position: 'relative', height: 20, margin: '0 8px' }}>
          {dateLabels.map((d, i) => (
            <span key={i} style={{
              position: 'absolute', left: d.pct + '%', transform: 'translateX(-50%)',
              fontSize: 9, color: 'var(--ds-muted)', whiteSpace: 'nowrap', top: 4,
            }}>
              {d.label}
            </span>
          ))}
        </div>
        <div />
      </div>

      {/* Righe */}
      {fasi.map((fase, i) => {
        const colore = STATUS_COLORS[fase.status] || '#F0AD4E';
        const start = new Date(fase.data_inizio_pianificata).getTime();
        const end = new Date(fase.data_fine_pianificata).getTime();
        const leftPct = ((start - minDate) / range) * 100;
        const widthPct = Math.max(3, ((end - start) / range) * 100);
        const pct = fase.percentuale_completamento;
        const isActive = fase.status === 'in_progress';

        return (
          <div key={fase.id} style={{
            display: 'grid', gridTemplateColumns: '140px 90px 1fr 100px',
            padding: '10px 16px', alignItems: 'center',
            borderBottom: i < fasi.length - 1 ? '1px solid var(--ds-border)' : 'none',
            background: isActive ? 'rgba(46,160,132,0.12)' : 'transparent',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: colore, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: isActive ? 500 : 400, color: 'var(--ds-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fase.nome}
              </span>
            </div>

            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: colore + '20', color: colore, fontWeight: 500, whiteSpace: 'nowrap', width: 'fit-content' }}>
              {STATUS_LABEL[fase.status]}
            </span>

            <div style={{ position: 'relative', height: 16, borderRadius: 4, margin: '0 8px' }}>
              {todayPct > 0 && todayPct < 100 && (
                <div style={{ position: 'absolute', top: -2, bottom: -2, left: todayPct + '%', width: 2, background: 'var(--ds-danger)', zIndex: 2, borderRadius: 1, opacity: 0.6 }} />
              )}
              <div style={{ position: 'absolute', top: 0, height: '100%', left: leftPct + '%', width: widthPct + '%', borderRadius: 4, background: colore + '25' }} />
              <div style={{ position: 'absolute', top: 0, height: '100%', left: leftPct + '%', width: (widthPct * pct / 100) + '%', borderRadius: 4, background: colore, minWidth: pct > 0 ? 3 : 0 }} />
            </div>

            <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--ds-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fase.responsabile_esterno || '—'}
            </div>
          </div>
        );
      })}

      {/* Legenda status */}
      <div style={{
        display: 'flex', gap: 14, padding: '10px 16px',
        borderTop: '1px solid var(--ds-border)', flexWrap: 'wrap',
      }}>
        {[
          { label: 'Completata', color: '#2EA084' },
          { label: 'In corso', color: '#4A7CFF' },
          { label: 'A rischio', color: '#F5A623' },
          { label: 'Pianificata', color: '#F0AD4E' },
          { label: 'Bloccata', color: '#E85D5D' },
        ].map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--ds-muted)' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Milestone Row con toggle/edit/delete ────────────────────────────────────

function MilestoneRow({ milestone: m, onToggle, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  // R2 — milestone da scadenziario nasce con data=None: '' per l'input controllato,
  // → null (non '') in salvataggio così il DateField nullable accetta il valore.
  const [form, setForm] = useState({ nome: m.nome, data: m.data || '', importo_sal: m.importo_sal || '' });

  const handleSave = () => {
    onUpdate({
      nome: form.nome,
      data: form.data || null,
      importo_sal: form.importo_sal ? parseFloat(form.importo_sal) : null,
    });
    setEditing(false);
  };

  if (editing) {
    return (
      <div style={{ padding: '10px 14px', borderRadius: 6, border: '1px solid var(--brand)', background: 'var(--chip)', display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 2 }}>
          <label style={labelStyle}>Nome</label>
          <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={inputStyle} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Data</label>
          <input type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} style={inputStyle} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Importo SAL</label>
          <input type="number" step="0.01" value={form.importo_sal} onChange={e => setForm({ ...form, importo_sal: e.target.value })} style={inputStyle} />
        </div>
        <button onClick={handleSave} style={{ ...btnPrimary, padding: '6px 14px', fontSize: 12 }}>Salva</button>
        <button onClick={() => setEditing(false)} style={{ ...btnCancel, padding: '6px 14px' }}>×</button>
      </div>
    );
  }

  return (
    <div style={{
      padding: '10px 14px', borderRadius: 6, border: '1px solid var(--ds-border)',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      {/* Toggle raggiunta */}
      <button onClick={onToggle} title={m.raggiunta ? 'Segna come non raggiunta' : 'Segna come raggiunta'} style={{
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
        border: m.raggiunta ? '2px solid var(--brand)' : '2px solid var(--ds-border)',
        background: m.raggiunta ? 'var(--brand)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: 12,
      }}>
        {m.raggiunta ? '✓' : ''}
      </button>
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 13, color: 'var(--ds-text)',
          textDecoration: m.raggiunta ? 'line-through' : 'none',
          opacity: m.raggiunta ? 0.6 : 1,
        }}>{m.nome}</div>
        <div style={{ fontSize: 11, color: 'var(--ds-muted)', fontStyle: m.data ? 'normal' : 'italic' }}>{m.data ? formatDate(m.data) : 'Data da definire'}</div>
      </div>
      {m.importo_sal && (
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          {Number(m.importo_sal).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
        </span>
      )}
      <button onClick={() => setEditing(true)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 14, padding: '2px 4px' }} title="Modifica">✏️</button>
      <button onClick={onDelete} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ds-danger)', fontSize: 14, padding: '2px 4px' }} title="Elimina">×</button>
    </div>
  );
}

// ── Date inline editor (onBlur → validazione + PATCH) ──────────────────────

function DateInlineEditor({ fase, onUpdateDate }) {
  const [inizio, setInizio] = useState(fase.data_inizio_pianificata);
  const [fine, setFine] = useState(fase.data_fine_pianificata);
  const [errore, setErrore] = useState('');

  useEffect(() => {
    setInizio(fase.data_inizio_pianificata);
    setFine(fase.data_fine_pianificata);
    setErrore('');
  }, [fase.data_inizio_pianificata, fase.data_fine_pianificata]);

  const handleBlur = (field) => {
    if (fine < inizio) {
      setErrore('La data fine deve essere successiva alla data inizio');
      return;
    }
    setErrore('');
    const original = field === 'data_inizio_pianificata' ? fase.data_inizio_pianificata : fase.data_fine_pianificata;
    const current = field === 'data_inizio_pianificata' ? inizio : fine;
    if (current !== original) {
      onUpdateDate(field, current);
    }
  };

  const handleStatusChange = (newStatus) => {
    onUpdateDate('status', newStatus);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', marginBottom: 4, borderBottom: '1px solid var(--ds-border)', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, color: 'var(--ds-muted)', minWidth: 35 }}>Stato:</span>
      <select value={fase.status} onChange={e => handleStatusChange(e.target.value)} style={{
        padding: '4px 8px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
        border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: STATUS_COLORS[fase.status] || 'var(--ds-text)',
        fontWeight: 500,
      }}>
        {Object.entries(STATUS_LABEL).map(([k, v]) => (
          <option key={k} value={k}>{v}</option>
        ))}
      </select>
      <span style={{ fontSize: 11, color: 'var(--ds-muted)', minWidth: 35, marginLeft: 8 }}>Date:</span>
      <input type="date" value={inizio}
        onChange={e => setInizio(e.target.value)}
        onBlur={() => handleBlur('data_inizio_pianificata')}
        style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--ds-border)', fontSize: 12, background: 'var(--ds-bg)', color: 'var(--ds-text)' }}
      />
      <span style={{ fontSize: 12, color: 'var(--ds-muted)' }}>→</span>
      <input type="date" value={fine}
        onChange={e => setFine(e.target.value)}
        onBlur={() => handleBlur('data_fine_pianificata')}
        style={{ padding: '4px 8px', borderRadius: 4, border: errore ? '1px solid var(--ds-danger)' : '1px solid var(--ds-border)', fontSize: 12, background: 'var(--ds-bg)', color: 'var(--ds-text)' }}
      />
      {errore && <span style={{ fontSize: 11, color: 'var(--ds-danger)' }}>{errore}</span>}
    </div>
  );
}

function MiniKpi({ label, value, color }) {
  return (
    <div style={{ padding: '10px 16px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--chip)' }}>
      <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-display)', color: color || 'var(--ink)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--ds-muted)', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
}

function FaseRow({ fase, canMoveUp, canMoveDown, onMoveUp, onMoveDown, index, isDragging, isDragOver, onDragStartFase, onDragOverFase, onDropFase, onDragEndFase, expanded, onToggle, onCompletaTask, onDelete, isEditing, onToggleEdit, onUpdateFase, showAddTask, onToggleAddTask, onAddTask }) {
  const colore = STATUS_COLORS[fase.status] || '#CBD5E0';
  const tasks = fase.tasks || [];
  const completate = tasks.filter(t => t.status === 'done').length;
  const arrowBtn = (enabled) => ({
    width: 24, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: 'none', background: 'transparent', padding: 0,
    cursor: enabled ? 'pointer' : 'default',
    color: enabled ? 'var(--ds-muted)' : 'var(--ds-border)',
  });

  return (
    <div style={{ border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', overflow: 'hidden', borderLeft: '4px solid ' + colore, opacity: isDragging ? 0.4 : 1, transition: 'opacity .12s' }}>
      <div
        onClick={onToggle}
        onDragOver={onDragOverFase}
        onDrop={onDropFase}
        style={{
        padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
        background: expanded ? 'var(--chip)' : 'var(--ds-bg)',
        boxShadow: isDragOver ? 'inset 0 3px 0 0 var(--brand)' : undefined,
      }}>
        {/* Handle trascinamento (riordino drag&drop) */}
        <span
          draggable
          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(index)); onDragStartFase(); }}
          onDragEnd={onDragEndFase}
          onClick={(e) => e.stopPropagation()}
          title="Trascina per riordinare"
          aria-label="Trascina per riordinare la fase"
          style={{ cursor: 'grab', color: 'var(--ds-muted)', display: 'flex', alignItems: 'center', flexShrink: 0, touchAction: 'none' }}
        >
          <GripVertical size={15} strokeWidth={1.8} />
        </span>
        {/* Riordino fase ↑/↓ */}
        <div style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <button aria-label="Sposta su" disabled={!canMoveUp}
            onClick={(e) => { e.stopPropagation(); if (canMoveUp && onMoveUp) onMoveUp(); }}
            style={arrowBtn(canMoveUp)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6" /></svg>
          </button>
          <button aria-label="Sposta giù" disabled={!canMoveDown}
            onClick={(e) => { e.stopPropagation(); if (canMoveDown && onMoveDown) onMoveDown(); }}
            style={arrowBtn(canMoveDown)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
          </button>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ds-text)' }}>{fase.nome}</div>
          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 2 }}>
            {formatDate(fase.data_inizio_pianificata)} → {formatDate(fase.data_fine_pianificata)}
            {fase.ritardo_giorni > 0 && <span style={{ color: 'var(--warn)', marginLeft: 8 }}>+{fase.ritardo_giorni}gg ritardo</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: colore + '20', color: colore, fontWeight: 500 }}>
            {STATUS_LABEL[fase.status]}
          </span>
          {tasks.length > 0 && <span style={{ fontSize: 11, color: 'var(--ds-muted)' }}>{completate}/{tasks.length}</span>}
          <div style={{ width: 60, height: 4, borderRadius: 2, background: 'var(--ds-border)' }}>
            <div style={{ height: '100%', borderRadius: 2, background: colore, width: fase.percentuale_completamento + '%' }} />
          </div>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '8px 16px 12px', borderTop: '1px solid var(--ds-border)' }}>
          {/* Form modifica fase inline */}
          {isEditing && (
            <EditFaseForm fase={fase} onSave={onUpdateFase} onCancel={onToggleEdit} />
          )}

          {/* Date inline editabili */}
          {!isEditing && (
            <DateInlineEditor fase={fase} onUpdateDate={(field, value) => onUpdateFase({ [field]: value })} />
          )}

          {tasks.map(task => (
            <div key={task.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
              borderBottom: '1px solid var(--ds-border)',
            }}>
              <button onClick={(e) => { e.stopPropagation(); if (task.status !== 'done') onCompletaTask(task.id); }}
                disabled={task.status === 'done'}
                style={{
                  width: 22, height: 22, borderRadius: '50%',
                  border: task.status === 'done' ? '2px solid var(--brand)' : '2px solid var(--ds-border)',
                  background: task.status === 'done' ? 'var(--brand)' : 'transparent',
                  cursor: task.status === 'done' ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 12, flexShrink: 0,
                }}>
                {task.status === 'done' ? '✓' : ''}
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: 'var(--ds-text)', textDecoration: task.status === 'done' ? 'line-through' : 'none', opacity: task.status === 'done' ? 0.6 : 1 }}>
                  {task.nome}
                </div>
                {task.completato_via_voce && <span style={{ fontSize: 10, color: 'var(--ds-muted)' }}>🎙️ via vocale</span>}
              </div>
              <span style={{ fontSize: 10, color: 'var(--ds-muted)' }}>{TASK_STATUS_LABEL[task.status]}</span>
            </div>
          ))}

          {showAddTask ? (
            <AddTaskInline onSubmit={onAddTask} onCancel={onToggleAddTask} />
          ) : (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={(e) => { e.stopPropagation(); onToggleAddTask(); }} style={btnSmall}>+ Aggiungi task</button>
              {!isEditing && (
                <button onClick={(e) => { e.stopPropagation(); onToggleEdit(); }} style={btnSmall}>Modifica fase</button>
              )}
              <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
                style={{ ...btnSmall, color: 'var(--ds-danger)', borderColor: '#E85D5D40' }}>Elimina fase</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Form Modifica Fase ─────────────────────────────────────────────────────

function EditFaseForm({ fase, onSave, onCancel }) {
  const [form, setForm] = useState({
    nome: fase.nome,
    data_inizio_pianificata: fase.data_inizio_pianificata,
    data_fine_pianificata: fase.data_fine_pianificata,
    costo_previsto: fase.costo_previsto || '',
    costo_effettivo: fase.costo_effettivo || '',
    descrizione: fase.descrizione || '',
    responsabile_esterno: fase.responsabile_esterno || '',
  });

  return (
    <div style={{ padding: 14, border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', marginBottom: 12, background: 'var(--chip)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)', marginBottom: 10 }}>Modifica fase</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Nome</label>
          <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Data inizio</label>
          <input type="date" value={form.data_inizio_pianificata} onChange={e => setForm({ ...form, data_inizio_pianificata: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Data fine</label>
          <input type="date" value={form.data_fine_pianificata} onChange={e => setForm({ ...form, data_fine_pianificata: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Costo previsto</label>
          <input type="number" step="0.01" value={form.costo_previsto} onChange={e => setForm({ ...form, costo_previsto: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Costo effettivo</label>
          <input type="number" step="0.01" value={form.costo_effettivo} onChange={e => setForm({ ...form, costo_effettivo: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Responsabile esterno</label>
          <input value={form.responsabile_esterno} onChange={e => setForm({ ...form, responsabile_esterno: e.target.value })} placeholder="Opzionale" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Descrizione</label>
          <input value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} placeholder="Opzionale" style={inputStyle} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button onClick={onCancel} style={btnCancel}>Annulla</button>
        <button disabled={!form.nome} onClick={() => onSave({
          ...form,
          costo_previsto: form.costo_previsto ? parseFloat(form.costo_previsto) : 0,
          costo_effettivo: form.costo_effettivo ? parseFloat(form.costo_effettivo) : 0,
        })} style={{ ...btnPrimary, padding: '8px 16px', fontSize: 12 }}>Salva modifiche</button>
      </div>
    </div>
  );
}
